import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { AlertType, FabricType, Error } from '../types/common';
import {
    CREATE_FABRICTYPE_ERROR_MESSAGE,
    DELETE_FABRICTYPE_ERROR_MESSAGE,
    FETCH_TYPESOFFABRIC_ERROR_MESSAGE,
    UPDATE_FABRICTYPE_ERROR_MESSAGE,
} from '../constants/messages';
import { showAlert } from './CommonSlice';
import { getCsrfToken } from '../utils/helpers';

export type FabricTypeState = {
    typesOfFabric: FabricType[];
    selectedFabricType: FabricType;
    isLoading: boolean;
    error: Error;
};

const initialState: FabricTypeState = {
    typesOfFabric: [],
    selectedFabricType: {
        id: '',
        name: '',
        description: '',
        url: '',
    },
    isLoading: false,
    error: {
        isError: false,
        message: '',
    },
};

const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

// Fetch typesOfFabric
export const fetchTypesOfFabric = createAsyncThunk('fabricType/fetchtypesOfFabric', async (_, { dispatch, rejectWithValue }) => {
    const response = await fetch(`${BASE_URL}/typesOfFabric`);

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: FETCH_TYPESOFFABRIC_ERROR_MESSAGE }));
        return rejectWithValue(FETCH_TYPESOFFABRIC_ERROR_MESSAGE);
    }

    const data = await response.json();

    // Преобразуем `_id` в `id` для совместимости с компонентом
    const typesOfFabric: FabricType[] = data.map((fabricType: any) => ({
        id: fabricType._id, // Преобразование _id в id
        name: fabricType.name,
        description: fabricType.description,
        url: fabricType.url,
    }));

    return typesOfFabric;
});

// Create fabricType
export const createFabricType = createAsyncThunk('fabricType/createFabricType', async (fabricType: Partial<FabricType>, { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${BASE_URL}/typesOfFabric`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
        body: JSON.stringify(fabricType),
    });

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: CREATE_FABRICTYPE_ERROR_MESSAGE }));
        return rejectWithValue(CREATE_FABRICTYPE_ERROR_MESSAGE);
    }

    const data = await response.json();

    // Преобразуем `_id` в `id`
    return { id: data.id, ...fabricType } as FabricType;
});

// Update fabricType
export const updateFabricType = createAsyncThunk('fabricType/updateFabricType', async (fabricType: FabricType, { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${BASE_URL}/typesOfFabric/${fabricType.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
        body: JSON.stringify({
            name: fabricType.name,
            description: fabricType.description,
            url: fabricType.url,
        }),
    });

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: UPDATE_FABRICTYPE_ERROR_MESSAGE }));
        return rejectWithValue(UPDATE_FABRICTYPE_ERROR_MESSAGE);
    }

    // Убедимся, что `id` сохраняется
    const data = await response.json();
    const updatedFabricType: FabricType = {
        id: fabricType.id,
        name: data.name ?? fabricType.name,
        description: data.description ?? fabricType.description,
        url: data.url ?? fabricType.url,
    };

    return updatedFabricType;
});

// Delete fabricType
export const deleteFabricType = createAsyncThunk('fabricType/deleteFabricType', async (id: FabricType['id'], { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${BASE_URL}/typesOfFabric/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
    });

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: DELETE_FABRICTYPE_ERROR_MESSAGE }));
        return rejectWithValue(DELETE_FABRICTYPE_ERROR_MESSAGE);
    }

    return id;
});

export const FabricTypeSlice = createSlice({
    name: 'fabricType',
    initialState,
    reducers: {
        selectFabricType: (state, action: PayloadAction<FabricType['id']>) => {
            const fabricType = state.typesOfFabric.find((fabricType) => fabricType.id === action.payload);

            if (!fabricType) return;

            state.selectedFabricType = fabricType;
        },

        removeSelectedFabricType: (state) => {
            state.selectedFabricType = initialState.selectedFabricType;
        },

        resetFabricTypeError: (state) => {},
    },

    extraReducers: (builder) => {
        builder.addCase(fetchTypesOfFabric.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(fetchTypesOfFabric.fulfilled, (state, { payload }) => {
            state.typesOfFabric = payload;
            state.isLoading = false;
        });

        builder.addCase(fetchTypesOfFabric.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(createFabricType.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(createFabricType.fulfilled, (state, { payload }) => {
            state.typesOfFabric.push(payload);
            state.isLoading = false;
        });

        builder.addCase(createFabricType.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(updateFabricType.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(updateFabricType.fulfilled, (state, { payload }) => {
            const index = state.typesOfFabric.findIndex((fabricType) => fabricType.id === payload.id);
            if (index < 0) return;
            state.typesOfFabric[index] = payload;
            state.isLoading = false;
        });

        builder.addCase(updateFabricType.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(deleteFabricType.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(deleteFabricType.fulfilled, (state, { payload }) => {
            const index = state.typesOfFabric.findIndex((fabricType) => fabricType.id === payload);
            state.typesOfFabric.splice(index, 1);
            state.isLoading = false;
        });

        builder.addCase(deleteFabricType.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });
    },
});

export const { selectFabricType, removeSelectedFabricType, resetFabricTypeError } = FabricTypeSlice.actions;

export default FabricTypeSlice.reducer;
